import classnames from 'classnames'
import { useRouter } from 'next-nprogress-bar'
import { CSSProperties, forwardRef, useRef } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { Skeleton } from '@src/quince-components/src'
import POSITIONS from '@src/quince-site-common/src/constants/positions'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'

import { trackingMetaData } from '../../collection.constants'
import { SubCollection } from '../../domains/Collection'

import styles from './subCollectionSection.module.scss'

const TRACKING_CATEGORY_NAVIGATION = 'navigation'

interface SubCollectionSectionProps {
  className?: string
  subCollectionData: SubCollection
  isFirstCollection?: boolean
  showBorderBottom?: boolean
  tracker: string
  shouldShowAllVariants?: boolean
  additionalAttributes?: Record<string, string>
  collectionGender: GenderName | undefined
  doHolidayCutoffSorting: boolean
  collectionSlug: string
  productViewTracker: string
  showSecondImageOnProductTiles?: boolean
  enableImageCarousel?: boolean
  showShimmerOnProductCards?: boolean
}

type makeGridPositionStyle = (options: {
  horizontalPosition: POSITIONS
  noOfRows: number
  sizeInNumberOfRows: number
  maxColumnPosition: number
}) => CSSProperties

const LEFT_POSITIONS = new Set([POSITIONS.BOTTOM_LEFT, POSITIONS.LEFT, POSITIONS.TOP_LEFT])
const RIGHT_POSITIONS = new Set([POSITIONS.BOTTOM_RIGHT, POSITIONS.RIGHT, POSITIONS.TOP_RIGHT])

const makeGridPositionStyle: makeGridPositionStyle = ({
  horizontalPosition,
  noOfRows,
  // sizeInNumberOfRows,
  maxColumnPosition,
}) => {
  return {
    gridRowStart: noOfRows === 1 ? 1 : noOfRows - 1,
    gridRowEnd: `span 2`,
    gridColumnStart: LEFT_POSITIONS.has(horizontalPosition) ? 1 : maxColumnPosition,
    gridColumnEnd: RIGHT_POSITIONS.has(horizontalPosition) ? maxColumnPosition : 1,
  }
}

const SubCollectionSection = forwardRef<HTMLDivElement, SubCollectionSectionProps>(
  (
    {
      subCollectionData,
      className,
      isFirstCollection = true,
      showBorderBottom,
      tracker,
      additionalAttributes,
      collectionGender,
      productViewTracker,
      showSecondImageOnProductTiles = false,
      enableImageCarousel = false,
      showShimmerOnProductCards = false,
    },
    _ref, // Renamed to _ref to match unused arg pattern
  ) => {
    const router = useRouter()
    const { deviceType } = useDeviceInfo()
    const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
    const isSvdEnabled = useIsSvdEnabled()
    const productCards = subCollectionData.productCards

    const imageRef = useRef<HTMLImageElement | null>(null)

    const maxWidth = getMaxWidthInPx({
      maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
      maxWidthDesktopInRem: styles.productCardWidth ?? '',
      deviceType: deviceType,
    })

    if (showShimmerOnProductCards) {
      return (
        <div className={styles.productCardsSkeletonWrapper} data-testid="product-card-skeleton">
          {new Array(isDesktop ? 8 : 4).fill(0).map((_, index) => (
            <Skeleton key={index} className={styles.productCardSkeleton} />
          ))}
        </div>
      )
    }

    return (
      <div
        data-testid="sub-collection-section-wrapper"
        data-content-type="sub-collection"
        className={classnames(styles.wrapper, className, styles.wrapper__new_clp_filters_experiment, {
          [styles.wrapper__hasPaddingBottom ?? '']: true,
          [styles.wrapper__hasBorderBottom ?? '']: isDesktop && showBorderBottom,
        })}
        ref={_ref}
      >
        <div
          className={classnames(styles.productCardsWrapper, styles.productCardsWrapper__new_clp_filters_experiment)}
          data-content-type="products"
        >
          {productCards?.map((productCard, idx) => {
            // TEMPORARY (STRFRNT-15810 · CLP SVD preview): on CLPs, SVD-flow users may receive SVD-shaped
            // cards from PL — render the native SVD card. `isPlSvdCard` shape-guards on anchor data, so a
            // legacy-shaped card (no anchor attributes) always falls through to the legacy ProductCard.
            // Remove once every PL environment emits the SVD shape unconditionally.
            if (isSvdEnabled && isPlSvdCard(productCard.product)) {
              return (
                <SvdProductCard
                  key={productCard.product.productId}
                  product={mapPlSvdCard(productCard.product as unknown as PlSvdCard)}
                  className={styles.productCard}
                  maxWidth={maxWidth}
                  productViewTracker={productViewTracker}
                  eagerLoadImage={isFirstCollection && idx < 4}
                  enableImageCarousel={enableImageCarousel}
                  productClickTrackConfig={{
                    trackInternal: true,
                    trackGA: true,
                    category: TRACKING_CATEGORY_NAVIGATION,
                    action: 'click_subcollection_product',
                    context: {
                      product_id: productCard.product.relatedIds.spid,
                      product_position: idx,
                      ...trackingMetaData.context,
                    },
                    value: productCard.product.slug,
                  }}
                />
              )
            }
            //TODO Handle runtime refresh of color order
            const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
            return (
              <ProductCard
                key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                productCard={productCard}
                className={styles.productCard}
                gender={collectionGender}
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                shouldShowMore
                productViewTracker={productViewTracker}
                productClickTrackConfig={{
                  trackInternal: true,
                  trackGA: true,
                  category: TRACKING_CATEGORY_NAVIGATION,
                  action: 'click_subcollection_product',
                  context: {
                    product_id: productCard.product.relatedIds.spid,
                    product_color: initialSelectedVariant?.displayConfig.value,
                    product_position: idx,
                    ...trackingMetaData.context,
                  },
                  value: productCard.product.slug,
                }}
                onShowMoreClick={({ selectedVariant }) => {
                  navigateToPDP({
                    slug: productCard.product.slug,
                    tracker: tracker && `${tracker}__${subCollectionData.title}__${idx}`,
                    selectedOption: selectedVariant?.displayConfig.value,
                    additionalAttributes,
                    router,
                  })
                }}
                additionalAttributes={additionalAttributes}
                maxWidth={maxWidth}
                selectorKey={isFirstCollection && idx === 0 ? QUINCE_BOT_LOCATORS.COLLECTION.MOVE_TO_PDP : undefined}
                eagerLoadImage={isFirstCollection && idx < 4}
                tracker={tracker && `${tracker}__${subCollectionData.title}__${idx}`}
                imageRef={imageRef}
                showSecondImageOnProductTiles={showSecondImageOnProductTiles}
                enableImageCarousel={enableImageCarousel}
                showWhiteGloveDeliveryTag
                isPageEligibleForHolidayTag={true}
              />
            )
          })}
        </div>
      </div>
    )
  },
)

SubCollectionSection.displayName = 'SubCollectionSection'

export default SubCollectionSection
